import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation } from '@apollo/client';
import { FIND_USER_ID_MUTATION, RESET_PASSWORD_MUTATION } from '../../apollo/queries/userQueries';
import '../css/FindCredentialsPage.css';

const FindCredentialsPage = () => {
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState('findId'); // 'findId' or 'resetPassword'
    const [isLoading, setIsLoading] = useState(false);
    
    // 아이디 찾기 폼 데이터
    const [findIdData, setFindIdData] = useState({
        name: '',
        phone: ''
    });
    
    // 비밀번호 재설정 폼 데이터
    const [resetPasswordData, setResetPasswordData] = useState({
        email: '',
        name: ''
    });

    // GraphQL 뮤테이션
    const [findUserId] = useMutation(FIND_USER_ID_MUTATION, {
        onCompleted: (data) => {
            setIsLoading(false);
            if (data.findUserId.success) {
                alert(data.findUserId.message);
                // 성공 시 로그인 페이지로 이동
                navigate('/');
            } else {
                alert(data.findUserId.message);
            }
        },
        onError: (error) => {
            setIsLoading(false);
            alert('아이디 찾기 중 오류가 발생했습니다: ' + error.message);
        }
    });

    const [resetPassword] = useMutation(RESET_PASSWORD_MUTATION, {
        onCompleted: (data) => {
            setIsLoading(false);
            if (data.resetPassword.success) {
                alert(data.resetPassword.message);
                // 성공 시 로그인 페이지로 이동
                navigate('/');
            } else {
                alert(data.resetPassword.message);
            }
        },
        onError: (error) => {
            setIsLoading(false);
            alert('비밀번호 재설정 중 오류가 발생했습니다: ' + error.message);
        }
    });

    // 아이디 찾기 입력 핸들러
    const handleFindIdChange = (e) => {
        const { name, value } = e.target;
        setFindIdData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    // 비밀번호 재설정 입력 핸들러
    const handleResetPasswordChange = (e) => {
        const { name, value } = e.target;
        setResetPasswordData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    // 아이디 찾기 제출
    const handleFindIdSubmit = (e) => {
        e.preventDefault();
        
        if (!findIdData.name.trim()) {
            alert('이름을 입력해주세요.');
            return;
        }
        
        if (!findIdData.phone.trim()) {
            alert('전화번호를 입력해주세요.');
            return;
        }

        setIsLoading(true);
        findUserId({
            variables: {
                name: findIdData.name.trim(),
                phone: findIdData.phone.trim()
            }
        });
    };

    // 비밀번호 재설정 제출
    const handleResetPasswordSubmit = (e) => {
        e.preventDefault();
        
        if (!resetPasswordData.email.trim()) {
            alert('이메일을 입력해주세요.');
            return;
        }
        
        if (!resetPasswordData.name.trim()) {
            alert('이름을 입력해주세요.');
            return;
        }

        // 이메일 형식 검증
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(resetPasswordData.email)) {
            alert('올바른 이메일 형식을 입력해주세요.');
            return;
        }

        setIsLoading(true);
        resetPassword({
            variables: {
                email: resetPasswordData.email.trim(),
                name: resetPasswordData.name.trim()
            }
        });
    };

    return (
        <div className="fcp-find-credentials-page">
            <div className="fcp-container">
                <div className="fcp-header">
                    <h1>아이디 / 비밀번호 찾기</h1>
                    <p>가입 시 입력한 정보를 통해 아이디를 찾거나 비밀번호를 재설정할 수 있습니다.</p>
                </div>

                <div className="fcp-tab-container">
                    <div className="fcp-tabs">
                        <button 
                            className={`fcp-tab ${activeTab === 'findId' ? 'active' : ''}`}
                            onClick={() => setActiveTab('findId')}
                        >
                            아이디 찾기
                        </button>
                        <button 
                            className={`fcp-tab ${activeTab === 'resetPassword' ? 'active' : ''}`}
                            onClick={() => setActiveTab('resetPassword')}
                        >
                            비밀번호 재설정
                        </button>
                    </div>

                    <div className="fcp-tab-content">
                        {activeTab === 'findId' ? (
                            <div className="fcp-find-id-form">
                                <h3>아이디 찾기</h3>
                                <p className="fcp-form-description">
                                    가입 시 입력한 이름과 전화번호를 입력해주세요.<br/>
                                    일치하는 계정의 아이디(이메일)를 등록된 이메일로 전송해드립니다.
                                </p>
                                
                                <form onSubmit={handleFindIdSubmit}>
                                    <div className="fcp-form-group">
                                        <label htmlFor="find-name">이름 *</label>
                                        <input
                                            type="text"
                                            id="find-name"
                                            name="name"
                                            value={findIdData.name}
                                            onChange={handleFindIdChange}
                                            placeholder="가입 시 입력한 이름을 입력하세요"
                                            disabled={isLoading}
                                        />
                                    </div>
                                    
                                    <div className="fcp-form-group">
                                        <label htmlFor="find-phone">전화번호 *</label>
                                        <input
                                            type="tel"
                                            id="find-phone"
                                            name="phone"
                                            value={findIdData.phone}
                                            onChange={handleFindIdChange}
                                            placeholder="가입 시 입력한 전화번호를 입력하세요 (예: 010-1234-5678)"
                                            disabled={isLoading}
                                        />
                                        <small className="fcp-field-help">
                                            일반 전화번호 또는 휴대폰 번호 모두 가능합니다.
                                        </small>
                                    </div>
                                    
                                    <button 
                                        type="submit" 
                                        className="fcp-submit-btn"
                                        disabled={isLoading}
                                    >
                                        {isLoading ? '처리 중...' : '아이디 찾기'}
                                    </button>
                                </form>
                            </div>
                        ) : (
                            <div className="fcp-reset-password-form">
                                <h3>비밀번호 재설정</h3>
                                <p className="fcp-form-description">
                                    가입 시 입력한 이메일과 이름을 입력해주세요.<br/>
                                    임시 비밀번호를 등록된 이메일로 전송해드립니다.
                                </p>
                                
                                <form onSubmit={handleResetPasswordSubmit}>
                                    <div className="fcp-form-group">
                                        <label htmlFor="reset-email">이메일 주소 *</label>
                                        <input
                                            type="email"
                                            id="reset-email"
                                            name="email"
                                            value={resetPasswordData.email}
                                            onChange={handleResetPasswordChange}
                                            placeholder="가입 시 사용한 이메일 주소를 입력하세요"
                                            disabled={isLoading}
                                        />
                                    </div>
                                    
                                    <div className="fcp-form-group">
                                        <label htmlFor="reset-name">이름 *</label>
                                        <input
                                            type="text"
                                            id="reset-name"
                                            name="name"
                                            value={resetPasswordData.name}
                                            onChange={handleResetPasswordChange}
                                            placeholder="가입 시 입력한 이름을 입력하세요"
                                            disabled={isLoading}
                                        />
                                    </div>
                                    
                                    <div className="fcp-security-notice">
                                        <h4>🔒 보안 안내</h4>
                                        <ul>
                                            <li>임시 비밀번호는 24시간 후 만료됩니다.</li>
                                            <li>로그인 후 반드시 새로운 비밀번호로 변경해주세요.</li>
                                            <li>타인에게 임시 비밀번호를 공유하지 마세요.</li>
                                        </ul>
                                    </div>
                                    
                                    <button 
                                        type="submit" 
                                        className="fcp-submit-btn"
                                        disabled={isLoading}
                                    >
                                        {isLoading ? '처리 중...' : '임시 비밀번호 발급'}
                                    </button>
                                </form>
                            </div>
                        )}
                    </div>
                </div>

                <div className="fcp-actions">
                    <button 
                        className="fcp-back-btn" 
                        onClick={() => navigate('/')}
                    >
                        로그인 페이지로 돌아가기
                    </button>
                </div>

                <div className="fcp-help">
                    <h4>도움이 필요하신가요?</h4>
                    <p>
                        계정 찾기에 문제가 있으시거나 추가 도움이 필요하시면<br/>
                        고객센터로 문의해주세요.
                    </p>
                    <div className="fcp-contact-info">
                        <p><strong>고객센터:</strong> 031-478-6301</p>
                        <p><strong>이메일:</strong> info@busbarworld.com</p>
                        <p><strong>운영시간:</strong> 평일 09:00~18:00 (주말 및 공휴일 휴무)</p>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default FindCredentialsPage; 